/* eslint-disable prettier/prettier */

/**
 * KitUtilCommon
 *
 * Generic utilities that don't fit conveniently in the following utils:
 * - KitAddress
 * - KitFormat
 * - KitUI
 * - KitUnitConversion
 * - KitValidation
 */

import queryString from 'query-string';
import { KeyMap } from '../constants';

export function decodeHTMLEntities(text: string): string {
  const entities = [
    ['amp', '&'],
    ['apos', '\''],
    ['#x27', '\''],
    ['#x2F', '/'],
    ['#39', '\''],
    ['#47', '/'],
    ['lt', '<'],
    ['gt', '>'],
    ['nbsp', ' '],
    ['quot', '"'],
  ];

  let decodedText = text;
  for (let i = 0, max = entities.length; i < max; i += 1) {
    const regex = `&${entities[i][0]};`;
    decodedText = decodedText.replace(new RegExp(regex, 'g'), entities[i][1]);
  }

  return decodedText;
}

export function getQueryObject(search: string): Record<string, unknown> {
  return queryString.parse(search);
}

export function getQueryParam(search: string, param: string): unknown {
  const parsedQuery = getQueryObject(search);
  if (!Object.prototype.hasOwnProperty.call(parsedQuery, param)) {
    return null;
  }
  return parsedQuery[param];
}

export function getQueryString(parsedQuery: Record<string, string>) {
  let retval = '';

  Object.keys(parsedQuery).forEach((key) => {
    if (!retval.length) {
      retval += '?';
    } else {
      retval += '&';
    }
    retval += encodeURIComponent(key);
    retval += '=';
    retval += encodeURIComponent(parsedQuery[key]);
  });
  return retval;
}

/**
 * Provides simple boolean comparison to test if one or more keys were pressed
 *
 * Usage:
 *  const onKeyUp = (e: KeyboardEvent) => {
 *     // test if key is UP or DOWN key
 *     if(isKey([KeyConstants.UP, KeyConstants.DOWN], e)) {
 *         // do something
 *      }
 *  }
 *
 * @param e
 */
export function isKey(keyToCompare: string | string[], e: KeyboardEvent): boolean {
  const key = e.key || e.keyCode || e.which;
  if (Array.isArray(keyToCompare)) {
    return keyToCompare.some((keyConst) => {
      return KeyMap[keyConst].includes(key);
    });
  }

  return KeyMap[keyToCompare].includes(key);
}

// forces number to fall within min/max
export const clamp = (number: number, min: number, max?: number): number => {
  if (!max) {
    return Math.max(number, min) === min ? number : min;
  }
  if (Math.min(number, min) === number) {
    return min;
  }
  if (Math.max(number, max) === number) {
    return max;
  }
  return number;
};

// MDN Docs: Testing for Availability (slightly modified from example)
// https://developer.mozilla.org/en-US/docs/Web/API/Web_Storage_API/Using_the_Web_Storage_API#testing_for_availability
export const localStorageAvailable = (): boolean => {
  try {
    const x = '__storage_test__';
    window.localStorage.setItem(x, x);
    window.localStorage.removeItem(x);
    return true;
    // eslint-disable-next-line
  } catch (err: any) {
    const e = err as DOMException;
    const everythingExceptFirefox = e.code === 22;
    const firefox = e.code === 1014;
    const quotaExceededError = e.name === 'QuotaExceededError';
    const quotaReachedError = e.name === 'NS_ERROR_DOM_QUOTA_REACHED';
    return (
      (everythingExceptFirefox || firefox || quotaExceededError || quotaReachedError) &&
      window.localStorage &&
      window.localStorage.length !== 0
    );
  }
};

export const makePermissionMap = (tasks: Array<string>) => {
  const mapArray = tasks.map<[string, boolean]>((task) => {
    return [task, true];
  });
  return new Map<string, boolean>(mapArray);
};

/**
 * @param delay (number)
 *
 * USAGE:
 * <FocusTrap focusTrapOptions={
 *     checkCanFocusTrap: focusTrapDelay(1000)
 * }>
 */

export const focusTrapDelay = (delay = 0) => {
  return (containers?: (HTMLElement | SVGElement)[]) =>
    new Promise<void>((resolve, reject) => {
      // if containers are not found, we can reject immediately
      if (!containers) {
        reject();
      } else {
        setTimeout(() => {
          resolve();
        }, delay);
      }
    });
};

export default {
  clamp,
  decodeHTMLEntities,
  isKey,
  getQueryObject,
  getQueryParam,
  getQueryString,
  localStorageAvailable,
};
